import axios from "axios";
import { API_BASE_URL } from "utils/api";
import { getToken } from "utils/auth";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const addLeadSchedule = async ({ leadId, scheduledTime, note }) => {
  const response = await api.post("/leads/add-schedule", {
    leadId,
    scheduledTime,
    note,
  });

  return response?.data;
};

export const editLeadSchedule = async ({ leadId, scheduleId, scheduledTime, note, scheduleStatus }) => {
  const response = await api.put("/leads/edit-schedule", {
    leadId,
    scheduleId,
    scheduledTime,
    note,
    scheduleStatus,
  });

  return response?.data;
};

export const addLeadNote = async ({ leadId, note }) => {
  const response = await api.post("/leads/add-note", {
    leadId,
    note,
  });

  return response?.data;
};

export const getLeadNoteScheduleDetails = async (leadId) => {
  const response = await api.get(
    `/leads/${encodeURIComponent(leadId)}/note-schedule-details`
  );
  return response?.data;
};

/**
 * Fetch a single lead's details
 * @param {string} leadId - The ID of the lead to fetch
 * @returns {Promise} - Response from the API containing lead details
 * @throws {Error} - If the fetch fails
 */
export const getLead = async (leadId) => {
  try {
    const url = `/leads/${encodeURIComponent(leadId)}`;
    const response = await api.get(url);

    if (response?.data?.status === false) {
      throw new Error(response?.data?.message || "Failed to fetch lead");
    }

    return response?.data;
  } catch (error) {
    console.error("Error in getLead:", error.message, error.response?.data);
    throw error;
  }
};

export const updateLeadNote = async (leadId, noteId, { note }) => {
  const response = await api.put(
    `/leads/${encodeURIComponent(leadId)}/note/${encodeURIComponent(noteId)}`,
    { note }
  );
  return response?.data;
};

export const removeLeadDocument = async (leadId, fileName) => {
  const response = await api.delete(`/leads/${encodeURIComponent(leadId)}/documents`, {
    data: { fileName }
  });
  return response?.data;
};

export const addLeadInstallment = async (leadId, payload) => {
  const response = await api.post(`/leads/${encodeURIComponent(leadId)}/installments`, payload);
  return response?.data;
};

export const deleteLeadNote = async (leadId, noteId) => {
  const response = await api.delete(
    `/leads/${encodeURIComponent(leadId)}/note/${encodeURIComponent(noteId)}`
  );
  return response?.data;
};

export const deleteLeadSchedule = async (leadId, scheduleId) => {
  const response = await api.delete(
    `/leads/${encodeURIComponent(leadId)}/schedule/${encodeURIComponent(
      scheduleId
    )}`
  );
  return response?.data;
};

export const updateLead = async (leadId, formData) => {
  const response = await api.put(
    `/leads/${encodeURIComponent(leadId)}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response?.data;
};

export const addSchedule = async (leadId, formData) => {
  const response = await api.put(
    `/leads/${encodeURIComponent(leadId)}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response?.data;
};

export const updateLeadWork = async (leadId, workId, payload) => {
  const response = await api.put(
    `/leads/${encodeURIComponent(leadId)}/works/${encodeURIComponent(workId)}`,
    payload
  );
  return response?.data;
};

export const createWork = async (payload) => {
  const worksApi = axios.create({
    baseURL: "http://localhost:3002",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });

  worksApi.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Convert payload to form-urlencoded format
  const formData = new URLSearchParams();
  formData.append("title", payload.title);
  formData.append("description", payload.description);
  formData.append("priority", payload.priority);
  if (payload.estimatedAmount !== null && payload.estimatedAmount !== undefined) {
    formData.append("estimatedAmount", payload.estimatedAmount);
  }
  if (payload.leadId) {
    formData.append("leadId", payload.leadId);
  }

  const response = await worksApi.post("/works", formData);
  return response?.data;
};

export const updateWork = async (workId, payload, isFormData = false) => {
  const worksApi = axios.create({
    baseURL: "http://localhost:3002",
    headers: isFormData
      ? {} // Let axios set Content-Type: multipart/form-data for FormData
      : { "Content-Type": "application/json" },
  });

  worksApi.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const response = await worksApi.put(`/works/${encodeURIComponent(workId)}`, payload);
  return response?.data;
};

export const deleteWorkAttachments = async (workId, attachmentIds) => {
  const worksApi = axios.create({
    baseURL: "http://localhost:3002",
    headers: {
      "Content-Type": "application/json",
    },
  });

  worksApi.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const response = await worksApi.delete(`/works/${encodeURIComponent(workId)}/attachments`, {
    data: { attachmentIds },
  });
  return response?.data;
};
